import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import SignaturePad from 'signature_pad';
import { ParticipantMatchField } from 'generated/event.types';
import { useSubmitParticipantSignatureMutation } from 'modules/events/graphql/mutations/submitParticipantSignature';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';

type UseParticipantSignatureModalParams = {
  participantId: string;
  eventId: string;
  matchField?: ParticipantMatchField;
  setOpen: (open: boolean) => void;
  onSigned: () => void;
};

const useParticipantSignatureModal = ({ participantId, eventId, matchField, setOpen, onSigned }: UseParticipantSignatureModalParams) => {
  const { t } = useTranslation();
  const signaturePadRef = useRef<SignaturePad | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [submitSignature, { loading: submitLoading }] = useSubmitParticipantSignatureMutation();

  const canvasRef = useCallback((canvas: HTMLCanvasElement | null) => {
    if (canvas) {
      const ratio = window.devicePixelRatio || 1;
      Object.assign(canvas, {
        width: canvas.offsetWidth * ratio,
        height: canvas.offsetHeight * ratio,
      });
      canvas.getContext('2d')?.scale(ratio, ratio);

      signaturePadRef.current = new SignaturePad(canvas);
      signaturePadRef.current.addEventListener('endStroke', () => {
        setIsEmpty(signaturePadRef.current?.isEmpty() ?? true);
      });
    } else if (signaturePadRef.current) {
      signaturePadRef.current.off();
      signaturePadRef.current = null;
    }
  }, []);

  const handleClear = useCallback(() => {
    signaturePadRef.current?.clear();
    setIsEmpty(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
      return;
    }

    const dataUrl = signaturePadRef.current.toDataURL('image/png');
    const image = dataUrl.replace('data:image/png;base64,', '');
    const success = await submitSignature(eventId, participantId, image, matchField);

    if (success) {
      toast.success(t('participant_signature_saved_success'));
      onSigned();
      setOpen(false);
    }
  }, [eventId, participantId, matchField, submitSignature, setOpen, onSigned, t]);

  const cancelButtonConfig = {
    buttonProps: {
      onClick: handleClear,
      buttonType: ButtonType.secondary,
    },
    label: t('participant_signature_clear_label'),
  };

  const submitButtonConfig = {
    buttonProps: {
      onClick: handleSubmit,
      buttonType: ButtonType.primary,
      disabled: isEmpty || submitLoading,
    },
    label: t('participant_signature_submit_label'),
    loading: submitLoading,
  };

  return {
    canvasRef,
    cancelButtonConfig,
    submitButtonConfig,
    handleClear,
    isEmpty,
    submitLoading,
  };
};

export default useParticipantSignatureModal;
